import * as XLSX from "xlsx";
import type { ParsedTestRun, ParsedWorkbook } from "./types";

type CellValue = string | number | boolean | Date | null | undefined;

const normalized = (value: CellValue): string =>
  String(value ?? "")
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const asText = (value: CellValue): string | null => {
  const text = String(value ?? "").trim();
  return text.length ? text : null;
};

const asNumber = (value: CellValue): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const number = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : null;
};

function sheetByName(workbook: XLSX.WorkBook, wanted: string): XLSX.WorkSheet {
  const actual = workbook.SheetNames.find((name) => name.trim().toLowerCase() === wanted.toLowerCase());
  if (!actual) throw new Error(`Required worksheet “${wanted}” was not found.`);
  const sheet = workbook.Sheets[actual];
  if (!sheet) throw new Error(`Worksheet “${wanted}” could not be read.`);
  return sheet;
}

function rowsOf(sheet: XLSX.WorkSheet): CellValue[][] {
  return XLSX.utils.sheet_to_json<CellValue[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
  });
}

function setupMap(rows: CellValue[][]): Record<string, string | number | boolean | null> {
  const output: Record<string, string | number | boolean | null> = {};
  for (const row of rows) {
    const labelIndex = row.findIndex((cell) => typeof cell === "string" && normalized(cell).length > 0);
    if (labelIndex < 0) continue;
    const label = asText(row[labelIndex]);
    if (!label || normalized(label) === "comments") continue;
    const valuesAfter = row.slice(labelIndex + 1).filter((cell) => cell !== null && cell !== undefined && cell !== "");
    const value = valuesAfter.at(-1);
    output[label] = value instanceof Date ? value.toISOString() : (value ?? null);
  }
  return output;
}

function getSetupValue(
  setup: Record<string, string | number | boolean | null>,
  label: string,
): string | number | boolean | null {
  const entry = Object.entries(setup).find(([key]) => normalized(key) === normalized(label));
  return entry?.[1] ?? null;
}

function findHeaderRow(rows: CellValue[][]): number {
  const index = rows.findIndex((row) =>
    row.some((cell) => /^result injector 1$/i.test(normalized(cell))) &&
    row.some((cell) => normalized(cell) === "result type"),
  );
  if (index < 0) throw new Error("The RESULTS worksheet does not contain the expected injector result headers.");
  return index;
}

function headerLookup(row: CellValue[]): Map<string, number> {
  const headers = new Map<string, number>();
  row.forEach((cell, index) => {
    const name = normalized(cell);
    if (name) headers.set(name, index);
  });
  return headers;
}

function column(headers: Map<string, number>, ...names: string[]): number | undefined {
  for (const name of names) {
    const found = headers.get(normalized(name));
    if (found !== undefined) return found;
  }
  return undefined;
}

function at(row: CellValue[], headers: Map<string, number>, ...names: string[]): CellValue {
  const index = column(headers, ...names);
  return index === undefined ? null : row[index];
}

function parseTestedAt(dateValue: CellValue, timeValue: CellValue, stampValue: CellValue): string | null {
  const stamp = asText(stampValue);
  if (stamp && /^\d{12}$/.test(stamp)) {
    const year = 2000 + Number(stamp.slice(0, 2));
    return `${year}-${stamp.slice(2, 4)}-${stamp.slice(4, 6)}T${stamp.slice(6, 8)}:${stamp.slice(8, 10)}:${stamp.slice(10, 12)}`;
  }
  if (dateValue instanceof Date) return dateValue.toISOString();
  const date = asText(dateValue);
  const time = asText(timeValue);
  return date ? `${date}${time ? ` ${time}` : ""}` : null;
}

export function parseWorkbookBytes(bytes: ArrayBuffer, filename: string, sourceHash: string): ParsedWorkbook {
  const workbook = XLSX.read(bytes, { type: "array", cellDates: true });
  const setup = setupMap(rowsOf(sheetByName(workbook, "SETUP")));
  const reference = asText(getSetupValue(setup, "Injector Reference"));
  if (!reference) throw new Error("SETUP is missing an Injector Reference.");

  const injectors: ParsedWorkbook["injectors"] = [];
  for (let position = 1; position <= 8; position += 1) {
    const serialNumber = asText(getSetupValue(setup, `Injector ID ${position}`));
    if (serialNumber) injectors.push({ position, serialNumber });
  }
  if (!injectors.length) throw new Error("SETUP does not contain any Injector ID values.");

  const resultRows = rowsOf(sheetByName(workbook, "RESULTS"));
  const headerRowIndex = findHeaderRow(resultRows);
  const headers = headerLookup(resultRows[headerRowIndex] ?? []);
  const tests: ParsedTestRun[] = [];

  resultRows.slice(headerRowIndex + 1).forEach((row, offset) => {
    const resultType = asText(at(row, headers, "Result Type"));
    if (!resultType) return;
    const readings = injectors.flatMap(({ position, serialNumber }) => {
      const value = asNumber(at(row, headers, `Result Injector ${position}`));
      const enabledValue = asNumber(at(row, headers, `Enabled Injector ${position}`));
      return value === null
        ? []
        : [{ position, serialNumber, value, enabled: enabledValue === null || enabledValue !== 0 }];
    });
    if (!readings.length) return;

    tests.push({
      sourceRow: headerRowIndex + offset + 2,
      resultType,
      repeatNumber: asNumber(at(row, headers, "Repeat Number")),
      testedAt: parseTestedAt(at(row, headers, "Date"), at(row, headers, "Time"), at(row, headers, "Date/Time Stamp")),
      testStep: asNumber(at(row, headers, "Test Step")),
      pressure: asNumber(at(row, headers, "Pressure")),
      pressureUnits: asText(at(row, headers, "Pressure Units")),
      temperature: asNumber(at(row, headers, "Temperature")),
      temperatureUnits: asText(at(row, headers, "Temperature Units")),
      injectorVoltage: asNumber(at(row, headers, "Inj V")),
      rpm: asNumber(at(row, headers, "RPM")),
      dutyPercent: asNumber(at(row, headers, "Duty (%)")),
      engineeringUnits: asText(at(row, headers, "Eng Units")),
      fuelFactor: asNumber(at(row, headers, "Fuel Factor")),
      targetType: asNumber(at(row, headers, "Target Type 0=AVG 1=Manual")),
      target: asNumber(at(row, headers, "Target")),
      tolerance: asNumber(at(row, headers, "Tolerance +/-")),
      average: asNumber(at(row, headers, "AVG")),
      variationPercent: asNumber(at(row, headers, "Result Variation +/-%")),
      orificeCode: asNumber(at(row, headers, "Orifice Code 1 = Cycles 2 = Temperature 4 = Time 8 = Pressure")),
      orificeFlow: asNumber(at(row, headers, "cc/min - Orifice Flowrite")),
      readings,
    });
  });

  if (!tests.length) throw new Error("RESULTS does not contain any populated injector result rows.");
  const testedAt = tests.map((test) => test.testedAt).filter((value): value is string => Boolean(value)).sort().at(-1) ?? null;

  return {
    filename,
    sourceHash,
    injectorReference: reference,
    injectors,
    setup,
    testplanName: asText(getSetupValue(setup, "Testplan Name")),
    testplanStructureVersion: asText(getSetupValue(setup, "Testplan Version - Structure")),
    testplanDataVersion: asText(getSetupValue(setup, "Testplan Version - Data")),
    testedAt,
    tests,
  };
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
